import { useEffect, useState, useRef, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import type { Point, Area } from 'react-easy-crop'
import {
  Box,
  Typography,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Avatar,
  Snackbar,
  Slider,
} from '@mui/material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ImageIcon from '@mui/icons-material/Image'
import { httpsCallable } from 'firebase/functions'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { functions, storage } from '../../config/firebase'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCommunityLinkStore } from '../../stores/communityLinkStore'
import {
  CommunityLink,
  CommunityLinkCategory,
  CommunityLinkGender,
  CommunityLinkPlatform,
  PlatformLabels,
  CategoryLabels,
  CountryLabels,
  GenderLabels,
} from '../../models/CommunityLink'

const communityLinkSchema = z.object({
  title: z.string().min(1, '請輸入標題'),
  description: z.string().min(1, '請輸入描述'),
  url: z.string().min(1, '請輸入網址'),
  imageUrl: z.string().optional(),
  platform: z.enum(['line', 'facebook', 'instagram', 'website', 'tiktok']),
  category: z.enum(['world_community', 'support_line', 'support_facebook', 'medical_resource', 'international_nurse']),
  order: z.number().min(0),
  isActive: z.boolean(),
  country: z.string().length(2).optional().or(z.literal('')),
  gender: z.enum(['male', 'female']).optional().or(z.literal('')),
})

type CommunityLinkForm = z.infer<typeof communityLinkSchema>

// Bookmarklet: runs in the IG page context to extract the profile picture CDN URL.
// The admin drags this link to their bookmark bar (one-time setup), then clicks it while on
// any IG profile page to copy the CDN URL, then pastes it into the "圖片網址" field.
const IG_BOOKMARKLET =
  `javascript:(function(){` +
  `var src=null;` +
  `var header=document.querySelector('header');` +
  `if(header){var imgs=header.querySelectorAll('img');` +
  `for(var i=0;i<imgs.length;i++){var s=imgs[i].src||'';` +
  `if(s&&(s.includes('cdninstagram.com')||s.includes('fbcdn.net'))){src=s;break;}}}` +
  `if(!src){var all=document.querySelectorAll('img');` +
  `for(var j=0;j<all.length;j++){var alt=all[j].alt||'';` +
  `if(alt.includes('profile picture')||alt.includes('大頭貼')){src=all[j].src;break;}}}` +
  `if(src){if(navigator.clipboard){` +
  `navigator.clipboard.writeText(src).then(function(){` +
  `alert('✅ 已複製大頭貼網址！\n請回到 Admin 貼到「圖片網址」欄位，再點「裁剪此圖」');` +
  `});}else{prompt('複製此網址後貼到 Admin：',src);}}` +
  `else{alert('❌ 找不到大頭貼，請確認在 Instagram 個人頁面並等圖片載入完成');}` +
  `})();`

const categoryTabs: { value: CommunityLinkCategory | 'all'; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'support_line', label: 'Line 社群' },
  { value: 'support_facebook', label: 'FB 社團' },
  { value: 'international_nurse', label: '國際護理師' },
  { value: 'medical_resource', label: '醫療專業資源庫' },
]

const platformColors: Record<CommunityLinkPlatform, 'success' | 'primary' | 'error' | 'info' | 'default'> = {
  line: 'success',
  facebook: 'primary',
  instagram: 'error',
  website: 'info',
  tiktok: 'default',
}

export default function CommunityLinkListPage() {
  const {
    links,
    loading,
    error,
    categoryFilter,
    countryFilter,
    genderFilter,
    setCategoryFilter,
    setCountryFilter,
    setGenderFilter,
    fetchLinks,
    addLink,
    updateLink,
    deleteLink,
    toggleActive,
  } = useCommunityLinkStore()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingLink, setEditingLink] = useState<CommunityLink | null>(null)
  const [fetchingOg, setFetchingOg] = useState<'og' | 'social' | 'proxy' | null>(null)
  const [fetchingOgId, setFetchingOgId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [fetchImageError, setFetchImageError] = useState<string | null>(null)
  const [showIgHelp, setShowIgHelp] = useState(false)
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null)

  // Crop state (inline, no nested dialog)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [cropMode, setCropMode] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const getCroppedBlob = (imageSrc: string, pixelCrop: Area): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => {
        const canvas = document.createElement('canvas')
        const size = Math.min(pixelCrop.width, pixelCrop.height, 400)
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, size, size)
        canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Canvas empty')), 'image/jpeg', 0.9)
      }
      image.onerror = reject
      image.src = imageSrc
    })

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CommunityLinkForm>({
    resolver: zodResolver(communityLinkSchema),
    defaultValues: {
      platform: 'website',
      category: 'support_line',
      order: 0,
      isActive: true,
    },
  })

  useEffect(() => {
    fetchLinks()
  }, [fetchLinks])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCategoryFilter(categoryTabs[newValue].value)
  }

  const currentTabIndex = categoryTabs.findIndex((t) => t.value === categoryFilter)

  const handleOpenDialog = (link?: CommunityLink) => {
    if (link) {
      setEditingLink(link)
      setImagePreview(link.imageUrl || null)
      reset({
        title: link.title,
        description: link.description,
        url: link.url,
        imageUrl: link.imageUrl || '',
        platform: link.platform,
        category: link.category,
        order: link.order,
        isActive: link.isActive,
        country: link.country || '',
        gender: link.gender || '',
      })
    } else {
      setEditingLink(null)
      setImagePreview(null)
      reset({
        title: '',
        description: '',
        url: '',
        imageUrl: '',
        platform: 'website',
        category: categoryFilter === 'all' ? 'support_line' : categoryFilter,
        order: links.length,
        isActive: true,
        country: '',
        gender: '',
      })
    }
    setDialogOpen(true)
  }

  const isBusy = fetchingOg !== null || uploading

  const handleCloseDialog = () => {
    if (isBusy) return
    setDialogOpen(false)
    setEditingLink(null)
    setImagePreview(null)
    setFetchImageError(null)
    setShowIgHelp(false)
    setCropMode(false)
    setCropSrc(null)
    reset()
  }

  const handleFetchSocialAvatar = () => {
    setFetchImageError(null)
    setShowIgHelp(true)
  }

  const handleFetchOgImage = async () => {
    const url = getValues('url')
    if (!url) return
    setFetchingOg('og')
    setFetchImageError(null)
    try {
      const fetchOgImageFn = httpsCallable<{ url: string }, { imageUrl: string | null }>(
        functions,
        'fetchOgImage'
      )
      const result = await fetchOgImageFn({ url })
      if (result.data.imageUrl) {
        setValue('imageUrl', result.data.imageUrl)
        setImagePreview(result.data.imageUrl)
      } else {
        setFetchImageError('找不到 OG 圖片，請改用上傳')
      }
    } catch (err) {
      console.error('OG image fetch failed:', err)
      setFetchImageError('擷取失敗，請改用上傳')
    } finally {
      setFetchingOg(null)
    }
  }

  const handleFetchOgImageForRow = async (id: string, url: string) => {
    if (!url) return
    setFetchingOgId(id)
    try {
      const fetchOgImageFn = httpsCallable<{ url: string }, { imageUrl: string | null }>(
        functions,
        'fetchOgImage'
      )
      const result = await fetchOgImageFn({ url })
      if (result.data.imageUrl) {
        await updateLink(id, { imageUrl: result.data.imageUrl })
        setSnackbar({ message: '縮圖已更新', severity: 'success' })
      } else {
        setSnackbar({ message: '找不到縮圖', severity: 'error' })
      }
    } catch (err) {
      console.error('OG image fetch failed:', err)
      setSnackbar({ message: '擷取縮圖失敗', severity: 'error' })
    } finally {
      setFetchingOgId(null)
    }
  }

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result
      if (typeof result === 'string') {
        setCropSrc(result)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedAreaPixels(null)
        setCropMode(true)
      }
    }
    reader.readAsDataURL(file)
    // Clear after read starts (not before)
    setTimeout(() => { event.target.value = '' }, 100)
  }

  const handleCropConfirm = async () => {
    if (!cropSrc || !croppedAreaPixels) return
    setUploading(true)
    setCropMode(false)
    try {
      const blob = await getCroppedBlob(cropSrc, croppedAreaPixels)
      const fileName = `community_thumbnails/${Date.now()}.jpg`
      const storageRef = ref(storage, fileName)
      await uploadBytes(storageRef, blob)
      const downloadUrl = await getDownloadURL(storageRef)
      setValue('imageUrl', downloadUrl)
      setImagePreview(downloadUrl)
    } catch (err) {
      console.error('Image upload failed:', err)
      setFetchImageError('上傳失敗，請重試')
    } finally {
      setUploading(false)
      setCropSrc(null)
    }
  }

  const handleCropCancel = () => {
    setCropMode(false)
    setCropSrc(null)
  }

  const handleProxyAndCrop = async () => {
    const url = getValues('imageUrl')
    if (!url) return
    setFetchingOg('proxy')
    setFetchImageError(null)
    try {
      const proxyFn = httpsCallable<{ url: string }, { storageUrl: string | null; error?: string }>(
        functions, 'proxyImageUrl'
      )
      const result = await proxyFn({ url })
      if (result.data.storageUrl) {
        setCropSrc(result.data.storageUrl)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setCroppedAreaPixels(null)
        setCropMode(true)
      } else {
        setFetchImageError(result.data.error || '下載失敗，請確認網址')
      }
    } catch (err) {
      console.error('proxyImageUrl failed:', err)
      setFetchImageError('下載失敗，請確認網址是否公開可存取')
    } finally {
      setFetchingOg(null)
    }
  }

  const onSubmit = async (data: CommunityLinkForm) => {
    try {
      // Sanitize empty strings to undefined for optional fields
      const payload = {
        ...data,
        country: data.country || undefined,
        gender: (data.gender as CommunityLinkGender) || undefined,
      }
      if (editingLink) {
        await updateLink(editingLink.id, payload)
      } else {
        await addLink(payload)
      }
      handleCloseDialog()
    } catch {
      // Error handled by store
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('確定要刪除此社群連結嗎？')) {
      await deleteLink(id)
    }
  }

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    await toggleActive(id, !currentActive)
  }

  const columns: GridColDef[] = [
    { field: 'order', headerName: '順序', width: 70, type: 'number' },
    {
      field: 'imageUrl',
      headerName: '圖片',
      width: 70,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Avatar
            src={params.value}
            variant="rounded"
            sx={{ width: 40, height: 40 }}
          >
            {params.row.title?.[0] || '?'}
          </Avatar>
        </Box>
      ),
      sortable: false,
    },
    { field: 'title', headerName: '標題', width: 180 },
    {
      field: 'platform',
      headerName: '平台',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={PlatformLabels[params.value as CommunityLinkPlatform] || params.value}
          color={platformColors[params.value as CommunityLinkPlatform] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'category',
      headerName: '分類',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={CategoryLabels[params.value as CommunityLinkCategory] || params.value}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'country',
      headerName: '國家',
      width: 100,
      renderCell: (params) => params.value ? (CountryLabels[params.value] || params.value) : '—',
    },
    {
      field: 'gender',
      headerName: '性別',
      width: 80,
      renderCell: (params) => params.value ? (GenderLabels[params.value as CommunityLinkGender] || params.value) : '—',
    },
    {
      field: 'description',
      headerName: '描述',
      width: 220,
    },
    {
      field: 'isActive',
      headerName: '狀態',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleActive(params.row.id, params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '操作',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          icon={fetchingOgId === params.row.id ? <CircularProgress size={18} /> : <RefreshIcon />}
          label="擷取縮圖"
          onClick={() => handleFetchOgImageForRow(params.row.id, params.row.url)}
          disabled={fetchingOgId === params.row.id}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="編輯"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="刪除"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ]

  if (loading && links.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          護理世界連結管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          新增連結
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Category filter tabs */}
      <Tabs
        value={currentTabIndex >= 0 ? currentTabIndex : 0}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
      >
        {categoryTabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {/* Country/Gender filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>國家</InputLabel>
          <Select
            value={countryFilter || ''}
            label="國家"
            onChange={(e) => setCountryFilter(e.target.value || null)}
          >
            <MenuItem value="">全部</MenuItem>
            {Object.entries(CountryLabels).map(([code, label]) => (
              <MenuItem key={code} value={code}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>性別</InputLabel>
          <Select
            value={genderFilter || ''}
            label="性別"
            onChange={(e) => setGenderFilter((e.target.value as CommunityLinkGender) || null)}
          >
            <MenuItem value="">全部</MenuItem>
            <MenuItem value="male">男性</MenuItem>
            <MenuItem value="female">女性</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Card>
        <DataGrid
          rows={links}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'order', sort: 'asc' }] },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
          }}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth disableEscapeKeyDown={isBusy}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingLink ? '編輯社群連結' : '新增社群連結'}
            {isBusy && <CircularProgress size={16} />}
          </DialogTitle>
          <DialogContent>
            <TextField
              {...register('title')}
              label="標題"
              fullWidth
              margin="normal"
              error={!!errors.title}
              helperText={errors.title?.message}
            />
            <TextField
              {...register('description')}
              label="描述"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              error={!!errors.description}
              helperText={errors.description?.message}
            />
            <TextField
              {...register('url')}
              label="網址"
              fullWidth
              margin="normal"
              error={!!errors.url}
              helperText={errors.url?.message}
            />
            {/* 縮圖區域 */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                縮圖
              </Typography>

              {cropMode && cropSrc ? (
                /* Inline crop UI */
                <Box>
                  <Box sx={{ position: 'relative', width: '100%', height: 280, bgcolor: '#111', borderRadius: 1, overflow: 'hidden' }}>
                    <Cropper
                      image={cropSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </Box>
                  <Box sx={{ px: 0.5, pt: 1, pb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">縮放</Typography>
                    <Slider value={zoom} min={1} max={3} step={0.05} onChange={(_, v) => setZoom(v as number)} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={handleCropCancel}>取消</Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                      onClick={handleCropConfirm}
                      disabled={uploading || !croppedAreaPixels}
                    >
                      確認裁剪並上傳
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  {/* 圖片預覽 */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 150,
                      border: '1px dashed #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                      overflow: 'hidden',
                      bgcolor: '#fafafa',
                    }}
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        onError={() => setImagePreview(null)}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 48, color: '#ccc' }} />
                    )}
                  </Box>

                  {/* 操作按鈕 */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={fetchingOg === 'og' ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                      onClick={handleFetchOgImage}
                      disabled={fetchingOg !== null || uploading}
                    >
                      OG 圖片
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={fetchingOg === 'social' ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                      onClick={handleFetchSocialAvatar}
                      disabled={fetchingOg !== null || uploading}
                    >
                      IG 大頭貼
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                      disabled={fetchingOg !== null || uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      上傳圖片
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={handleFileSelected}
                    />
                  </Box>

                  {showIgHelp && (
                    <Alert
                      severity="info"
                      sx={{ mb: 1 }}
                      onClose={() => setShowIgHelp(false)}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Instagram 因平台 IP 限制，無法從伺服器端自動擷取大頭貼
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        請選擇以下其中一種方式：
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        方法 A — Bookmarklet（快速）
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                        1. 將下方按鈕拖曳到瀏覽器書籤列（一次性設定）<br />
                        2. 在瀏覽器開啟 Instagram 個人頁面，等大頭貼載入<br />
                        3. 點書籤列中的「IG 頭貼擷取器」→ 自動複製圖片網址<br />
                        4. 回到這裡，貼到下方「圖片網址」欄位，點「裁剪此圖」
                      </Typography>
                      <Box sx={{ mb: 1.5 }}>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a
                          href={IG_BOOKMARKLET}
                          style={{
                            display: 'inline-block',
                            padding: '5px 14px',
                            background: '#e1306c',
                            color: '#fff',
                            borderRadius: 4,
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            cursor: 'grab',
                            userSelect: 'none',
                          }}
                          onClick={(e) => e.preventDefault()}
                          title="拖曳此連結到瀏覽器書籤列"
                          draggable
                        >
                          📷 IG 頭貼擷取器
                        </a>
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          ← 拖曳到書籤列
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        方法 B — 手動上傳
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        在 Instagram 上長按大頭貼 → 在新分頁開啟圖片 → 複製網址貼到下方；或直接點「上傳圖片」
                      </Typography>
                    </Alert>
                  )}

                  {fetchImageError && (
                    <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setFetchImageError(null)}>
                      {fetchImageError}
                    </Alert>
                  )}

                  {/* 手動輸入 URL */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      {...register('imageUrl')}
                      label="圖片網址 (選填)"
                      fullWidth
                      size="small"
                      placeholder="https://example.com/image.png"
                      onChange={(e) => {
                        register('imageUrl').onChange(e)
                        setImagePreview(e.target.value || null)
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      sx={{ whiteSpace: 'nowrap', mt: 0.1 }}
                      startIcon={fetchingOg === 'proxy' ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                      disabled={isBusy || !imagePreview}
                      onClick={handleProxyAndCrop}
                    >
                      裁剪此圖
                    </Button>
                  </Box>
                </>
              )}
            </Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>平台</InputLabel>
              <Controller
                name="platform"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="平台">
                    <MenuItem value="line">LINE</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="website">網站</MenuItem>
                    <MenuItem value="tiktok">TikTok</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>分類</InputLabel>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="分類">
                    <MenuItem value="support_line">Line 社群</MenuItem>
                    <MenuItem value="support_facebook">FB 社團</MenuItem>
                    <MenuItem value="international_nurse">國際護理師</MenuItem>
                    <MenuItem value="medical_resource">醫療專業資源庫</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>國家 (選填)</InputLabel>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="國家 (選填)">
                    <MenuItem value="">未指定</MenuItem>
                    {Object.entries(CountryLabels).map(([code, label]) => (
                      <MenuItem key={code} value={code}>{label}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>性別 (選填)</InputLabel>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="性別 (選填)">
                    <MenuItem value="">未指定</MenuItem>
                    <MenuItem value="male">男性</MenuItem>
                    <MenuItem value="female">女性</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <TextField
              {...register('order', { valueAsNumber: true })}
              label="排序"
              type="number"
              fullWidth
              margin="normal"
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="啟用"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={isBusy}>取消</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : '儲存'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>

    </Box>
  )
}
